import React from 'react'
import { useParams } from 'react-router-dom'
import { AssetDateFilter } from '../asset-date-filter/asset-date-filter'
import { BUSINESS_DETAILS, CREATIVE_REQUEST_TRENDLINE } from '../../../constants/routes'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'
import { mapAnalyzeData } from '../../../containers/utils'
import { useState } from 'react'
import './asset-usage-reporting.scss'
import { t } from '../../../utils/i18n'
import { CanBeAnything } from '../../../types'
import { MediaData, CreativeData } from '../../molecules/usage-reporting/usage-reporting'
import CustomTable from '../../molecules/table/table'
import { AnalyzeData, AnalyzeTable } from '../../molecules/analyze-table/AnalayzeTable'
import { Button } from '../../atoms/button/button'
import { ReactComponent as Download } from '../../../assets/svgs/download.svg'
import { showToast } from '../../atoms/toaster/toaster'

interface AssetUsageReportingProps {
  accountId: string
}

// On Component Load, getDateRange function is being called.

export const AssetUsageReporting = ({ accountId }: AssetUsageReportingProps) => {
  const { id: businessIdParam } = useParams()
  const { user } = useAuth()

  const [analyzeData, setAnalyzeData] = useState<AnalyzeData[]>()
  const [mediaData, setMediaData] = useState<MediaData | null>(null)
  const [creativeData, seCreativeData] = useState<CreativeData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const callAnalyzeAPI = async (
    accountId: string,
    businessIdParam: string,
    startDate: string,
    endDate: string
  ) => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${businessIdParam}/analyze/data?adAccountId=${accountId}&startDate=${startDate}&endDate=${endDate}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }
      const analyzeDatePromise = await axios(config)
      return analyzeDatePromise
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const callMediaAPI = async () => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${businessIdParam}/media/usage?adAccountId=${accountId}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }
      const mediaData = axios(config)
      return mediaData
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const callCreativeAPI = async () => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${businessIdParam}/creative/usage?adAccountId=${accountId}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }
      const creative = axios(config)
      return creative
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const getDateRange = async (startDate: string, endDate: string) => {
    if (businessIdParam) {
      const analyzeAPIPromise = callAnalyzeAPI(accountId, businessIdParam, startDate, endDate)
      const mediaAPIPromise = callMediaAPI()
      const creativePromise = callCreativeAPI()
      const [analyzeDataResponse, mediaDataResponse, creativeResponse]: [
        CanBeAnything,
        CanBeAnything,
        CanBeAnything,
      ] = await Promise.allSettled([analyzeAPIPromise, mediaAPIPromise, creativePromise])
      setAnalyzeData(mapAnalyzeData(analyzeDataResponse.value?.data?.data))
      if (mediaDataResponse?.value?.data?.data?.data) {
        setMediaData(mediaDataResponse?.value?.data?.data?.data)
      }
      if (creativeResponse?.value?.data?.data?.data) {
        seCreativeData(creativeResponse?.value?.data?.data?.data)
      }
    }
  }

  const columns = [
    { key: 'particular', label: t('particular'), flexGrow: 1 },
    { key: 'to.meta', label: t('to.meta'), flexGrow: 1 },
    { key: 'bm.status', label: t('bm.status'), flexGrow: 1 },
  ]

  const data = mediaData
    ? [
        {
          particular: t('batches'),
          'to.meta': mediaData.published_batch_count,
          'bm.status': mediaData.total_batch_count,
        },
        {
          particular: t('campaigns'),
          'to.meta': mediaData.published_campaign_count,
          'bm.status': mediaData.total_campaign_count,
        },
        {
          particular: t('ads'),
          'to.meta': mediaData.published_ad_count,
          'bm.status': mediaData.total_ad_count,
        },
      ]
    : []
  const Creativecolumns = [
    { key: 'particular', label: t('particular'), flexGrow: 1 },
    { key: 'insights', label: t('Insights'), flexGrow: 1 },
  ]
  const creativeTableData = creativeData
    ? [
        {
          particular: 'No of Creative Batches',
          insights: `${creativeData?.totalNumberOfNonApprovedBatches} (Started) / ${creativeData?.totalNumberOfApprovedBatches} (Approved)`,
        },
        {
          particular: 'Average no of AdGroups per Batch',
          insights: creativeData?.avgNumberOfAdGroupsPerBatch,
        },
        // {
        //   particular: 'Average Approval Time',
        //   insights: '5 min (dummy data)',
        // },
        {
          particular: 'Creative Analysis Requests Services',
          insights: `${creativeData?.totalRequestedCreativeRequests} (Requested) / ${creativeData?.totalCompletedCreativeRequests} (Ready)`,
        },
        {
          particular: 'Approved vs Rejected',
          insights: `${creativeData?.totalApprovedAdGroups} (Approved) / ${creativeData?.totalRejectedAdGroups} (Rejected)`,
        },
        {
          particular: 'Total Collaborative Users',
          insights: creativeData?.totalCollabrativeUsers,
        },
      ]
    : []
  const exportClick = async () => {
    try {
      const config = {
        method: 'POST',
        url: `${CREATIVE_REQUEST_TRENDLINE}`,
        data: {
          parameters: {
            adAccountId: accountId,
          },
        },
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const result = await axios(config)
      const reqId = result?.data?.data?.data?.requestId
      pollingForRequest(reqId)
      showToast({
        message: 'Success',
        description: t('Request Received!,Your file will auto download.'),
      })
      //  return result
    } catch {
      console.log('Unable to create export request')
      showToast({
        message: t('Request Failed!, Please try again.'),
        isError: true,
      })
    }
  }

  const pollingForRequest = async (reqId: CanBeAnything) => {
    const interval = 5000 // 5 seconds
    const maxDuration = 60000 // 1 minute
    const startTime = Date.now()

    const poll = async () => {
      try {
        const config = {
          method: 'get',
          url: `${CREATIVE_REQUEST_TRENDLINE}/${reqId}`,
          headers: user?.isSSO
            ? {
                'x-user': user?.email,
                'x-access-token': user?.tokens?.accessToken?.token,
                'x-source': 'sso',
              }
            : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
        }

        const result = await axios(config)
        const presignedURL = result?.data?.data?.data?.presignedUrl
        if (presignedURL) {
          // Stop polling and download the file
          clearInterval(pollingInterval)
          window.location.href = presignedURL // This will trigger the download
          showToast({
            message: t('File Downloaded Successfully!'),
          })
          return
        }

        // Check if the maximum duration has been reached
        if (Date.now() - startTime >= maxDuration) {
          clearInterval(pollingInterval)
          console.log('Polling stopped after 1 minute without finding presignedURL')
          showToast({
            message: t('Request Failed!, Please try again.'),
            isError: true,
          })
        }
      } catch (error) {
        console.log('Unable to create session Id', error)
        showToast({
          message: t('Request Failed!, Please try again.'),
          isError: true,
        })
      }
    }

    const pollingInterval = setInterval(poll, interval)
    poll() // Initial call to start polling immediately
  }
  return (
    <div className="drawer-body-container">
      <AssetDateFilter getDateRange={getDateRange} />
      <div>
        <h2 style={{ margin: '1rem' }}>{t('analyze')}</h2>
        <AnalyzeTable analyzeData={analyzeData} isLoading={isLoading} />
      </div>
      <div>
        <div className="creative-table">
          <h2>{t('Creative')}</h2>
          <Button
            ButtonIcon={<Download />}
            label="Export"
            buttonWithText={true}
            removeDefaultCss
            // disabled={true}
            // ariaLabel="go-back"
            // className="right-btn"
            handleClick={() => exportClick()}
          />
        </div>

        <CustomTable data={creativeTableData} columns={Creativecolumns} isLoading={isLoading} />
      </div>
      <h2 style={{ margin: '1rem' }}>{t('media')}</h2>
      <CustomTable data={data} columns={columns} isLoading={isLoading} />
    </div>
  )
}
